import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import './PublicTicketPage.css';

const isActiveTicketStatus = (status) => String(status || '').toUpperCase() === 'ISSUED';

const normalizeAmpInfo = (amp) => {
  const source = amp || {};
  return {
    eventDate: String(source.event_date || '').trim(),
    section: String(source.section || source.section_name || '').trim(),
    row: String(source.row || '').trim(),
    seat: String(source.seat_number ?? source.seat ?? '').trim(),
  };
};

function PublicTicketCollectionPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [walletLoadingId, setWalletLoadingId] = useState(null);
  const [walletError, setWalletError] = useState('');
  const [walletAddedMap, setWalletAddedMap] = useState({});
  const [nowTs, setNowTs] = useState(Date.now());
  const [geoState, setGeoState] = useState({
    status: 'locating',
    label: '',
  });
  const animationRefs = useRef({});

  const fetchCollection = useCallback(async () => {
    try {
      const res = await api.getPublicTicketCollection(token);
      if (res?.success && res?.data) {
        setData(res.data);
        const nextWalletState = {};
        (res.data.tickets || []).forEach((ticket) => {
          nextWalletState[ticket.ticket_code] = Boolean(ticket.wallet_added);
        });
        setWalletAddedMap(nextWalletState);
      } else {
        setError(res?.error?.message || 'Tickets not found');
      }
    } catch (err) {
      setError(err.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCollection();
  }, [fetchCollection]);

  useEffect(() => {
    const clockId = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(clockId);
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGeoState({ status: 'unsupported', label: 'Location unavailable' });
      return;
    }

    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (cancelled) return;
        const lat = Number(position.coords?.latitude || 0);
        const lng = Number(position.coords?.longitude || 0);
        setGeoState({
          status: 'ready',
          label: `${lat.toFixed(2)}, ${lng.toFixed(2)}`,
        });
      },
      () => {
        if (cancelled) return;
        setGeoState({ status: 'denied', label: 'Location permission denied' });
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 5 * 60 * 1000,
      }
    );

    return () => {
      cancelled = true;
    };
  }, []);

  // Animated red scan line for each QR
  useEffect(() => {
    if (!data?.tickets) return;

    const animate = (ticketCode, timestamp, start) => {
      if (!start) start = timestamp;
      const duration = 2000;
      const elapsed = (timestamp - start) % duration;
      const progress = elapsed / duration;
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      const refs = animationRefs.current[ticketCode];
      if (!refs || !refs.qrWrap || !refs.scanLine) return;
      const qrHeight = refs.qrWrap.offsetHeight;
      const y = eased * (qrHeight - 4);
      refs.scanLine.style.transform = `translateY(${y}px)`;
      refs.rafId = requestAnimationFrame((ts) => animate(ticketCode, ts, start));
    };

    const ticketCodes = data.tickets.map(t => t.ticket_code);
    const refsSnapshot = animationRefs.current;

    ticketCodes.forEach((code) => {
      const refs = refsSnapshot[code];
      if (refs && refs.qrWrap && refs.scanLine) {
        refs.rafId = requestAnimationFrame((ts) => animate(code, ts, null));
      }
    });

    return () => {
      ticketCodes.forEach((code) => {
        const refs = refsSnapshot[code];
        if (refs?.rafId) cancelAnimationFrame(refs.rafId);
      });
    };
  }, [data]);

  const setRef = (ticketCode, type, el) => {
    if (!animationRefs.current[ticketCode]) {
      animationRefs.current[ticketCode] = {};
    }
    animationRefs.current[ticketCode][type] = el;
  };

  const handleAppleWallet = (ticket) => {
    if (api.isIOSInAppBrowser && api.isIOSInAppBrowser()) {
      setWalletError('Apple Wallet requires Safari. Open this page in Safari, then try again.');
      return;
    }

    const url = api.getPublicAppleWalletUrl(ticket.ticket_id, ticket.ticket_code);
    setWalletError('');
    setWalletAddedMap((prev) => ({ ...prev, [ticket.ticket_code]: true }));

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || '');
    if (isIOS) {
      window.location.assign(url);
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleGoogleWallet = async (ticket) => {
    setWalletLoadingId(ticket.ticket_code);
    setWalletError('');
    try {
      const res = await api.getPublicGoogleWalletPass(ticket.ticket_id, ticket.ticket_code);
      if (res?.success && res?.save_url) {
        setWalletAddedMap((prev) => ({ ...prev, [ticket.ticket_code]: true }));
        window.open(res.save_url, '_blank', 'noopener,noreferrer');
      } else {
        setWalletError(res?.error?.message || 'Failed to create Google Wallet pass');
      }
    } catch (err) {
      setWalletError(err.message || 'Failed to create Google Wallet pass');
    } finally {
      setWalletLoadingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const isAmphitheater = (ticket) => {
    return !!ticket.amphitheater || 
      (ticket.ticket_type_name || '').toLowerCase().includes('amphitheater') ||
      (ticket.ticket_type_name || '').toLowerCase().includes('pacific');
  };

  const timeLabel = new Date(nowTs).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const zoneLabel = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local time';
  const countdown = 30 - (Math.floor(nowTs / 1000) % 30);

  if (loading) {
    return (
      <div className="ptp-container">
        <div className="ptp-loading">
          <div className="ptp-spinner" />
          <p>Loading your tickets…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ptp-container">
        <div className="ptp-error">
          <div className="ptp-error-icon">⚠</div>
          <h2>Tickets Not Found</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const tickets = data.tickets || [];
  const ampTickets = tickets.filter(isAmphitheater);
  const festivalTickets = tickets.filter(t => !isAmphitheater(t));

  const renderTicket = (ticket) => (
    <div key={ticket.ticket_code} className="ptp-collection-ticket">
      {(() => {
        const amp = normalizeAmpInfo(ticket.amphitheater);
        const hasAmpInfo = amp.section || amp.row || amp.seat || amp.eventDate;
        return (
          <>
      {ticket.gift_label && (
        <div className="ptp-gift-label">
          <span className="ptp-gift-icon">🎁</span>
          <span className="ptp-gift-text">{ticket.gift_label}</span>
        </div>
      )}

      <h3 className="ptp-ticket-type">{ticket.ticket_type_name}</h3>

      {hasAmpInfo && (
        <div className="ptp-amp-info">
          {amp.eventDate && (
            <div className="ptp-amp-row">
              <span className="ptp-amp-label">Date</span>
              <span className="ptp-amp-value">{formatDate(amp.eventDate)}</span>
            </div>
          )}
          <div className="ptp-amp-row">
            <span className="ptp-amp-label">Section</span>
            <span className="ptp-amp-value">{amp.section || '—'}</span>
          </div>
          <div className="ptp-amp-row">
            <span className="ptp-amp-label">Row</span>
            <span className="ptp-amp-value">{amp.row || '—'}</span>
          </div>
          <div className="ptp-amp-row">
            <span className="ptp-amp-label">Seat</span>
            <span className="ptp-amp-value">{amp.seat || '—'}</span>
          </div>
        </div>
      )}

      {!hasAmpInfo && ticket.valid_days && Array.isArray(ticket.valid_days) && (
        <div className="ptp-valid-days">
          {ticket.valid_days.map((day, i) => (
            <div key={i} className="ptp-day-badge">{formatDate(day)}</div>
          ))}
        </div>
      )}

      {ticket.qr_code && (
        <div className="ptp-qr-section">
          {isActiveTicketStatus(ticket.status) && !walletAddedMap[ticket.ticket_code] && (
            <div className="ptp-live-timer">
              <div className="ptp-live-timer-top">
                <span className="ptp-live-dot" />
                <span className="ptp-live-time">{timeLabel}</span>
                <span className="ptp-live-countdown">Refresh {String(countdown).padStart(2, '0')}s</span>
              </div>
              <div className="ptp-live-meta">
                <span>{zoneLabel}</span>
                {geoState.status === 'ready' && <span>Geo: {geoState.label}</span>}
                {geoState.status !== 'ready' && geoState.label && <span>{geoState.label}</span>}
              </div>
            </div>
          )}
          <div
            className="ptp-qr-frame"
            ref={(el) => setRef(ticket.ticket_code, 'qrWrap', el)}
          >
            <img src={ticket.qr_code} alt="Ticket QR Code" className="ptp-qr-image" />
            <div
              className="ptp-scan-line"
              ref={(el) => setRef(ticket.ticket_code, 'scanLine', el)}
            />
          </div>
          <p className="ptp-qr-hint">Present this QR code at the gate</p>
        </div>
      )}

      <div className="ptp-ticket-code">
        <span className="ptp-code-label">Ticket Code</span>
        <span className="ptp-code-value">{ticket.ticket_code}</span>
      </div>

      <div className="ptp-wallet-buttons">
        <button className="ptp-wallet-badge-btn" onClick={() => handleAppleWallet(ticket)}>
          <img src="/apple.webp" alt="Add to Apple Wallet" className="ptp-wallet-badge" />
        </button>
        <button
          className="ptp-wallet-badge-btn"
          onClick={() => handleGoogleWallet(ticket)}
          disabled={walletLoadingId === ticket.ticket_code}
        >
          {walletLoadingId === ticket.ticket_code ? 'Creating…' : (
            <img
              src="/enUS_add_to_google_wallet_add-wallet-badge.svg"
              alt="Add to Google Wallet"
              className="ptp-wallet-badge"
            />
          )}
        </button>
      </div>
      {walletError && walletLoadingId === null && (
        <p className="ptp-wallet-error">{walletError}</p>
      )}
          </>
        );
      })()}
    </div>
  );

  return (
    <div className="ptp-container">
      <div className="ptp-card ptp-collection-card">
        <div className="ptp-logo">
          <img src="/logo.png" alt="OC MENA Festival" />
        </div>

        <h1 className="ptp-collection-title">Your Tickets</h1>
        <p className="ptp-collection-subtitle">
          {tickets.length} ticket{tickets.length !== 1 ? 's' : ''} for {data.email}
        </p>

        {festivalTickets.length > 0 && (
          <div className="ptp-section-group">
            <h2 className="ptp-section-heading">Festival Tickets</h2>
            {festivalTickets.map(renderTicket)}
          </div>
        )}

        {ampTickets.length > 0 && (
          <div className="ptp-section-group">
            <h2 className="ptp-section-heading">Amphitheater Tickets</h2>
            {ampTickets.map(renderTicket)}
          </div>
        )}

        <div className="ptp-optional-links">
          <a href="/signup" className="ptp-opt-link">Create an account</a>
          <span className="ptp-opt-divider">·</span>
          <a href="https://ocmenafestival.com/app" className="ptp-opt-link">Download the app</a>
        </div>

        <div className="ptp-footer">
          <p>© 2026 MENA Events, Inc. All Rights Reserved.</p>
          <p className="ptp-venue">OC Fair & Event Center · 88 Fair Drive · Costa Mesa, CA 92626</p>
        </div>
      </div>
    </div>
  );
}

export default PublicTicketCollectionPage;
